import React, { createContext, useContext } from 'react';
import { useGlobalChat } from '../hooks/useGlobalChat';
import { UseGlobalChatReturn } from '../types/globalChat';

/**
 * Context for global chat state management
 * Provides app-wide access to the global chat functionality
 */
const GlobalChatContext = createContext<UseGlobalChatReturn | undefined>(undefined);

/**
 * Provider component for global chat state
 *
 * Wrap your app (or the portion that needs chat access) with this provider
 * to enable global chat functionality throughout the component tree.
 *
 * @example
 * ```tsx
 * // In App.tsx or a top-level component
 * <GlobalChatProvider>
 *   <YourApp />
 * </GlobalChatProvider>
 * ```
 */
export function GlobalChatProvider({ children }: { children: React.ReactNode }) {
  const globalChat = useGlobalChat();

  return (
    <GlobalChatContext.Provider value={globalChat}>
      {children}
    </GlobalChatContext.Provider>
  );
}

/**
 * Hook to access global chat state and functions from any component
 *
 * Must be used within a GlobalChatProvider.
 *
 * @returns The global chat state and control functions
 * @throws Error if used outside of GlobalChatProvider
 *
 * @example
 * ```tsx
 * function ChatButton() {
 *   const { state, toggleChat, openChat } = useGlobalChatContext();
 *
 *   return (
 *     <IconButton onClick={toggleChat}>
 *       <ChatIcon />
 *       {state.isOpen ? 'Close' : 'Open'} Chat
 *     </IconButton>
 *   );
 * }
 * ```
 *
 * @example
 * ```tsx
 * function ChatPanel() {
 *   const {
 *     state,
 *     isLoading,
 *     isSending,
 *     error,
 *     startConversation,
 *     sendMessage,
 *     closeChat,
 *   } = useGlobalChatContext();
 *
 *   if (!state.isOpen) return null;
 *
 *   return (
 *     <div>
 *       {state.messages.map(msg => (
 *         <Message key={msg.id} {...msg} />
 *       ))}
 *       <MessageInput onSend={sendMessage} disabled={isSending} />
 *     </div>
 *   );
 * }
 * ```
 */
export function useGlobalChatContext(): UseGlobalChatReturn {
  const context = useContext(GlobalChatContext);
  if (context === undefined) {
    throw new Error('useGlobalChatContext must be used within a GlobalChatProvider');
  }
  return context;
}

export { GlobalChatContext };
